/**
 * Phase 3C: Op Note Ingestion Hook
 * 
 * Handles saving, replacing, and managing immutable operative notes
 * with full audit trail support.
 */

import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { 
  PostOpState, 
  OpNoteSourceType,
} from '@/lib/phase3/types';
import { ALL_DEMO_OP_NOTES, DemoOpNoteType } from '@/lib/demo/demoOpNotes';

interface UseOpNoteIngestionOptions {
  caseId: string | null;
  onStateChange?: (newState: PostOpState) => void;
}

interface OpNoteData {
  rawText: string;
  sourceType: OpNoteSourceType;
  uploadedAt: string;
  uploadedBy?: string;
}

interface OpNoteIngestionResult {
  opNote: OpNoteData | null;
  isLoading: boolean;
  isSaving: boolean;
  error: Error | null;
  hasNote: boolean;
  
  // Actions
  saveNote: (text: string, sourceType: OpNoteSourceType) => Promise<boolean>;
  replaceNote: (text: string, sourceType: OpNoteSourceType, reason: string) => Promise<boolean>;
  loadDemoNote: (noteType: DemoOpNoteType) => void;
  clearDemoNote: () => void;
  
  // Demo-specific
  demoNoteType: DemoOpNoteType | null;
  availableDemoNotes: Array<{ type: DemoOpNoteType; title: string; cpt: string }>;
}

export function useOpNoteIngestion({
  caseId,
  onStateChange,
}: UseOpNoteIngestionOptions): OpNoteIngestionResult {
  const { isDemoMode } = useDemoMode();
  const [opNote, setOpNote] = useState<OpNoteData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [demoNoteType, setDemoNoteType] = useState<DemoOpNoteType | null>(null);

  // Get available demo notes for selection
  const availableDemoNotes = Object.values(ALL_DEMO_OP_NOTES).map(note => ({
    type: note.type,
    title: note.title,
    cpt: note.primaryCpt,
  }));

  // Load a demo op note (demo mode only)
  const loadDemoNote = useCallback((noteType: DemoOpNoteType) => {
    if (!isDemoMode) return;
    
    const demoNote = ALL_DEMO_OP_NOTES[noteType];
    if (!demoNote) return;

    setDemoNoteType(noteType);
    setOpNote({
      rawText: demoNote.opNoteText.trim(),
      sourceType: 'EHR_STUB',
      uploadedAt: new Date().toISOString(),
      uploadedBy: 'demo-user',
    });
    
    // Trigger state change to note_uploaded
    onStateChange?.('note_uploaded');
  }, [isDemoMode, onStateChange]);

  // Clear demo note
  const clearDemoNote = useCallback(() => {
    if (!isDemoMode) return;
    setOpNote(null);
    setDemoNoteType(null);
  }, [isDemoMode]);

  // Save operative note (immutable after save)
  const saveNote = useCallback(async (
    text: string,
    sourceType: OpNoteSourceType
  ): Promise<boolean> => {
    if (!text.trim()) {
      setError(new Error('Operative note text cannot be empty'));
      return false;
    }

    // Demo mode: just update local state
    if (isDemoMode) {
      setOpNote({
        rawText: text.trim(),
        sourceType,
        uploadedAt: new Date().toISOString(),
        uploadedBy: 'demo-user',
      });
      onStateChange?.('note_uploaded');
      return true;
    }

    if (!caseId) {
      setError(new Error('No case ID provided'));
      return false;
    }

    setIsSaving(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();

      // Check if note already exists (immutability check)
      const { data: existing } = await supabase
        .from('authorization_requests')
        .select('original_op_note_text')
        .eq('id', caseId)
        .single();

      if (existing?.original_op_note_text) {
        setError(new Error('Op note already exists. Use "Replace Note" to update.'));
        return false;
      }

      // Save the note - use 'paste' for db enum
      const dbSourceType = sourceType === 'PASTE' ? 'paste' 
        : sourceType === 'UPLOAD_PDF' ? 'upload' 
        : sourceType === 'UPLOAD_IMAGE' ? 'upload' 
        : 'ehr';
      
      const { error: updateError } = await supabase
        .from('authorization_requests')
        .update({
          original_op_note_text: text.trim(),
          op_note_source_type: dbSourceType as 'paste' | 'upload' | 'ehr',
          op_note_uploaded_at: new Date().toISOString(),
          op_note_uploaded_by: user?.id,
          post_op_state: 'note_uploaded',
        })
        .eq('id', caseId);

      if (updateError) throw updateError;

      // Log the action
      if (user) {
        await supabase.from('authorization_activity_log').insert([{
          authorization_id: caseId,
          actor_id: user.id,
          actor_type: 'system',
          action: 'Op note uploaded',
          details: {
            source_type: sourceType,
            text_length: text.trim().length,
          },
        }]);
      }

      setOpNote({
        rawText: text.trim(),
        sourceType,
        uploadedAt: new Date().toISOString(),
        uploadedBy: user?.id,
      });

      onStateChange?.('note_uploaded');
      return true;
    } catch (err) {
      console.error('Failed to save op note:', err);
      setError(err as Error);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [caseId, isDemoMode, onStateChange]);

  // Replace operative note (with audit trail)
  const replaceNote = useCallback(async (
    text: string,
    sourceType: OpNoteSourceType,
    reason: string
  ): Promise<boolean> => {
    if (!text.trim()) {
      setError(new Error('Operative note text cannot be empty'));
      return false;
    }

    if (!reason.trim()) {
      setError(new Error('Reason for replacement is required'));
      return false;
    }

    // Demo mode: just update local state
    if (isDemoMode) {
      setOpNote({
        rawText: text.trim(),
        sourceType,
        uploadedAt: new Date().toISOString(),
        uploadedBy: 'demo-user',
      });
      return true;
    }

    if (!caseId) {
      setError(new Error('No case ID provided'));
      return false;
    }

    setIsSaving(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();

      // Get the existing note for audit log
      const { data: existing } = await supabase
        .from('authorization_requests')
        .select('original_op_note_text, op_note_source_type')
        .eq('id', caseId)
        .single();

      // Log the replacement BEFORE updating (audit trail)
      if (user) {
        await supabase.from('authorization_activity_log').insert([{
          authorization_id: caseId,
          actor_id: user.id,
          actor_type: 'link_ops',
          action: 'Op note REPLACED',
          details: {
            reason,
            previous_source_type: existing?.op_note_source_type,
            previous_text_length: existing?.original_op_note_text?.length || 0,
            new_source_type: sourceType,
            new_text_length: text.trim().length,
            replaced_at: new Date().toISOString(),
          },
        }]);
      }

      // Convert source type to db enum
      const dbSourceType = sourceType === 'PASTE' ? 'paste' 
        : sourceType === 'UPLOAD_PDF' ? 'upload' 
        : sourceType === 'UPLOAD_IMAGE' ? 'upload' 
        : 'ehr';
      
      // Update with new note
      const { error: updateError } = await supabase
        .from('authorization_requests')
        .update({
          original_op_note_text: text.trim(),
          op_note_source_type: dbSourceType as 'paste' | 'upload' | 'ehr',
          op_note_uploaded_at: new Date().toISOString(),
          op_note_uploaded_by: user?.id,
          // Reset state to note_uploaded since we have a new note
          post_op_state: 'note_uploaded',
        })
        .eq('id', caseId);

      if (updateError) throw updateError;

      setOpNote({
        rawText: text.trim(),
        sourceType,
        uploadedAt: new Date().toISOString(),
        uploadedBy: user?.id,
      });

      onStateChange?.('note_uploaded');
      return true;
    } catch (err) {
      console.error('Failed to replace op note:', err);
      setError(err as Error);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [caseId, isDemoMode, onStateChange]);

  return {
    opNote,
    isLoading,
    isSaving,
    error,
    hasNote: !!opNote?.rawText,
    saveNote,
    replaceNote,
    loadDemoNote,
    clearDemoNote,
    demoNoteType,
    availableDemoNotes,
  };
}
